import React, { PropsWithChildren } from 'react'
import { Image } from '@shared/components/image'
import { Section } from '@shared/components/section'
import { Button } from '@shared/componentsStyled/Button'
import { useSelectDevice } from '@shared/hooks/use-select-device'
import MainBlockAndornment from '@shared/svg/main-block-adornment.svg'
import styled, { DefaultTheme } from 'styled-components'

import { Props } from './model'

export const View = ({
	data: {
		title,
		subtitle,
		textItems = [],
		description,
		descriptionClassName,
		buttonText,
		lightButtonText,
		footDisclaimer,
		footnote,
		faceImageName,
		imageAlt,
		customImage,
		isActiveAndornment,
		jsonData,
		// isAverageImg,
	},
	className,
	adornmentClassname,
	onLightButtonClick,
	buttonProps,
	// imageAdornmentWrapClassName,
	imageClassname,
}: PropsWithChildren<Props>) => {
	const name = useSelectDevice(faceImageName)

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { ref, ...buttonExProps } = buttonProps || {}
	return (
		<Section>
			<ViewRoot className={className}>
				<ViewContent>
					<ViewContentWrap>
						{!!title && <TextHeader>{title}</TextHeader>}
						{!!subtitle && <TextSubHeader>{subtitle}</TextSubHeader>}
						{!!textItems.length && (
							<ViewTextItems>
								{textItems.map((item, idx) => (
									<TextItem key={idx}>{item}</TextItem>
								))}
							</ViewTextItems>
						)}
						{(!!description || !!buttonText) && (
							<ViewButtonRow>
								{!!description && (
									<TextDescription className={descriptionClassName}>{description}</TextDescription>
								)}
								{!!buttonText && <Button {...buttonExProps}>{buttonText}</Button>}
							</ViewButtonRow>
						)}
						{!!lightButtonText && !!onLightButtonClick && (
							<TextLink onClick={onLightButtonClick}>{lightButtonText}</TextLink>
						)}
					</ViewContentWrap>
					{footnote && <TextFootnote>{footnote}</TextFootnote>}
				</ViewContent>
				<ViewImage>
					<ViewImageWrap>
						{!!isActiveAndornment && <ImageAdornment className={adornmentClassname} />}
						{!!customImage && customImage}
						{!!faceImageName && <ImageFace className={imageClassname} name={name} alt={imageAlt} />}
					</ViewImageWrap>
				</ViewImage>
				{!!jsonData?.backgroundImage && (
					<ViewBackgroundImage>
						<Image name={jsonData?.backgroundImage} />
					</ViewBackgroundImage>
				)}
			</ViewRoot>
			{footDisclaimer}
		</Section>
	)
}

const getTextShadow = (theme: DefaultTheme) => ({
	textShadow: `0 0 10px ${theme.colors.lightmint}`,
})

const ViewRoot = styled.div(({ theme }) => ({
	backgroundColor: theme.colors.lightmint,
	borderRadius: 40,
	display: 'flex',
	marginBottom: 60,
	marginTop: 25,
	overflow: 'hidden',
	position: 'relative',
}))

const ViewContent = styled.div(({ theme }) => ({
	padding: 40,
	flex: 1,
	alignSelf: 'stretch',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	zIndex: 2,
	'& :last-child': {
		marginBottom: 0,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		paddingRight: 0,
	},
}))

const ViewContentWrap = styled.div({
	flex: 1,
	alignSelf: 'stretch',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
})

const ViewImage = styled.div(({ theme }) => ({
	flex: 1,
	alignSelf: 'flex-end',
	alignItems: 'flex-end',
	display: 'none',
	zIndex: 1,
	[theme.mediaQueries.mediaTabletToDesktop]: {
		height: 'calc(100% - 110px)',
		aspectRatio: '1',
		display: 'block',
		position: 'absolute',
		bottom: 0,
		right: 0,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		minHeight: 512,
		display: 'flex',
	},
}))

const ViewBackgroundImage = styled.div({
	width: '100%',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'absolute',
	zIndex: 0,
})

const TextHeader = styled.h1(({ theme }) => ({
	...getTextShadow(theme),
	...theme.typography.header30_36,
	marginBottom: 25,
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		marginBottom: 20,
	},
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header56_60,
	},
}))

const TextSubHeader = styled.h2(({ theme }) => ({
	...getTextShadow(theme),
	marginBottom: 25,
	...theme.typography.text18_24,
	[theme.mediaQueries.mediaTabletToDesktop]: {
		width: '50%',
		marginRight: 'auto',
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		marginBottom: 20,
	},
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
	},
}))

const ViewTextItems = styled.div(({ theme }) => ({
	...getTextShadow(theme),
	marginBottom: 40,
	[theme.mediaQueries.mediaTabletToDesktop]: {
		width: '50%',
		marginRight: 'auto',
	},
}))

const TextItem = styled.p(({ theme }) => ({
	...theme.typography.text16_22,
	marginBottom: 10,
	'&:last-child': {
		marginBottom: 0,
	},
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
	},
}))

const ViewButtonRow = styled.div(({ theme }) => ({
	display: 'flex',
	marginBottom: 40,
	flexDirection: 'column',
	alignItems: 'flex-start',
	[theme.mediaQueries.mediaTabletToDesktop]: {
		width: '50%',
		marginRight: 'auto',
	},
	[theme.mediaQueries.mediaMinWidthTablet]: {
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexDirection: 'row-reverse',
	},
}))

const TextDescription = styled.p(({ theme }) => ({
	...getTextShadow(theme),
	...theme.typography.text16_22,
	opacity: 0.5,
	flex: 1,
	marginLeft: 0,
	marginBottom: 40,
	[theme.mediaQueries.mediaMinWidthTablet]: {
		marginLeft: 20,
		marginBottom: 0,
	},
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
	},
}))

const TextFootnote = styled.p(({ theme }) => ({
	...getTextShadow(theme),
	opacity: 0.5,
	marginTop: 40,
	display: 'none',
	...theme.typography.text12_16,
	[theme.mediaQueries.mediaMinWidthTablet]: {
		display: 'block',
	},
	[theme.mediaQueries.mediaTabletToDesktop]: {
		width: '50%',
		marginRight: 'auto',
	},
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text16_22,
	},
}))

const TextLink = styled(TextFootnote)(({ theme }) => ({
	...getTextShadow(theme),
	cursor: 'pointer',
	color: theme.colors.main,
	opacity: 1,
}))

const ViewImageWrap = styled.div({
	width: '100%',
	aspectRatio: '1',
	position: 'relative',
})

const ImageAdornment = styled(MainBlockAndornment)(({ theme }) => ({
	width: '100%',
	height: '100%',
	position: 'absolute',
	bottom: 0,
	right: 0,
	color: theme.colors.main,
}))

const ImageFace = styled(Image)({
	position: 'absolute',
	right: 0,
	bottom: 0,
	width: '100%',
	height: '100%',
	objectFit: 'contain',
	objectPosition: 'bottom right',
})
